// Cloudflare Turnstile bot check for public forms. Renders nothing unless
// REACT_APP_TURNSTILE_SITE_KEY was set at build time (the backend's
// TURNSTILE_SECRET_KEY must be set together with it — see
// backend/signature_backend/turnstile.py).
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useI18n } from '../../i18n';
import './Turnstile.css';

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_URL;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => { scriptPromise = null; reject(new Error('turnstile_load_failed')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// onToken(token) fires with a fresh token, and with '' when it expires or
// fails. The ref exposes reset() for after a rejected submit.
export const Turnstile = forwardRef(function Turnstile({ onToken }, ref) {
  const { lang } = useI18n();
  const box = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current);
      onTokenRef.current('');
    },
  }), []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let cancelled = false;
    loadScript().then((turnstile) => {
      if (cancelled || !box.current) return;
      widgetId.current = turnstile.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        language: lang,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(''),
        'error-callback': () => onTokenRef.current(''),
      });
    }).catch(() => {
      // Blocked by an extension or offline: the backend lets the submit
      // through only if it can't reach Cloudflare either, so surface nothing
      // here and let the server decide.
    });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetId.current !== null) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [lang]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div className="turnstile" ref={box} />;
});
